import {
    EditableEntity,
    ICreatableEntityCollection,
    IEditableEntity,
    IPropEventBroker,
    IReadablePropEventBroker,
} from '@zdrbm/zdr-native-tools';
import moment from 'moment';
import { ExtendedRunStatus, ExtendedRunStatusType, IConversationRun } from './ConversationRun';
import { PiError } from './pi-errors/PiError';

export enum ConversationStatus {
    Idle = 'Idle',
    WaitingForResponse = 'WaitingForResponse',
}

export interface IConversation extends IEditableEntity {
    addRun(run: IConversationRun): void;
    changeRunStatus(runId: string, status: ExtendedRunStatusType): void;
    status: IReadablePropEventBroker<ConversationStatus>;
    title: IReadablePropEventBroker<string | undefined>;
    timestamp: IReadablePropEventBroker<string | null>;
    removeLastRun(): void;

    setTitle(title: string | undefined): void;
    runs: ICreatableEntityCollection<IConversationRun>;
}

export class Conversation extends EditableEntity<keyof IConversation> implements IConversation {
    runs: ICreatableEntityCollection<IConversationRun>;
    status: IPropEventBroker<ConversationStatus>;
    title: IPropEventBroker<string | undefined>;
    timestamp: IReadablePropEventBroker<string | null>;

    constructor(
        id: string,
        title: string | undefined,
        runs: IConversationRun[],
        timestamp: string | undefined,
    ) {
        super(id);
        this.status = this.createPropEventBroker('status', ConversationStatus.Idle);
        this.runs = this.createEntityCollection<IConversationRun>('runs', runs);
        this.title = this.createPropEventBroker('title', title);
        this.timestamp = this.createPropEventBroker('timestamp', this.getTimestamp(timestamp));

        this.attachEvents();
        this.calculateState();
    }

    setTitle(title: string | undefined): void {
        this.title.set(title);
    }

    attachEvents() {
        this.runs.itemChanged.reo(() => this.calculateState());
        this.runs.itemsAdded.reo(() => this.calculateState());
    }

    private hasPendingRuns(): boolean {
        return this.runs
            .getOrderedItems()
            .some((run) =>
                [ExtendedRunStatus.PENDING, ExtendedRunStatus.RUNNING].some(
                    (status) => run.status.get() === status,
                ),
            );
    }

    private calculateState(): void {
        const hasPendingRuns = this.hasPendingRuns();
        this.status.set(
            hasPendingRuns ? ConversationStatus.WaitingForResponse : ConversationStatus.Idle,
        );
    }

    changeRunStatus(runId: string, status: ExtendedRunStatusType): void {
        const run = this.runs.getItem(runId);
        if (run) {
            run.changeStatus(status);
        } else {
            throw new PiError(`Run with id ${runId} not found`);
        }
    }

    addRun(run: IConversationRun): void {
        this.runs.addItems(undefined, run);
    }

    removeLastRun(): void {
        const orderedRuns = this.runs.getOrderedItems();
        if (orderedRuns.length > 0) {
            const lastRun = orderedRuns[orderedRuns.length - 1];
            this.runs.removeItem(lastRun.getId());
        }
    }

    getTimestamp(timestamp?: string): string | null {
        if (!timestamp) return null;

        const now = moment().utc();
        const date = moment.utc(timestamp).local();
        const diffMinutes = now.diff(date, 'minutes');
        const diffHours = now.diff(date, 'hours');
        const diffDays = now.diff(date, 'days');

        // Less than 1 minute
        if (diffMinutes < 1) {
            return 'now';
        }

        // Less than 1 hour
        if (diffMinutes < 60) {
            return `${diffMinutes}min`;
        }

        // Less than 1 day
        if (diffHours < 24) {
            return `${diffHours}h`;
        }

        // Less than 1 week
        if (diffDays < 7) {
            return date.format('ddd');
        }

        // After a week
        return date.format('MM/DD');
    }

    getEntityName(): string {
        return 'Conversation';
    }
}
